import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button, TextField, Divider, Chip, Avatar, ButtonBase, FormControlLabel, Switch } from '@mui/material';
import { Rating } from '@mui/material';

export interface ReviewModalProps {
  open: boolean;
  productName?: string;
  productImage?: string;
  price?: number;
  date?: string;
  productId?: string;
  loading?: boolean;
  rating: number | null;
  title: string;
  comment: string;
  onChangeRating: (value: number | null) => void;
  onChangeTitle: (value: string) => void;
  onChangeComment: (value: string) => void;
  // Privacy
  userShowAlias: boolean;
  onChangeUserShowAlias: (value: boolean) => void;
  // Category ratings
  priceRating: number;
  accuracyRating: number;
  deliveryRating: number;
  packagingRating: number;
  communicationRating: number;
  qualityRating: number;
  onChangePriceRating: (value: number) => void;
  onChangeAccuracyRating: (value: number) => void;
  onChangeDeliveryRating: (value: number) => void;
  onChangePackagingRating: (value: number) => void;
  onChangeCommunicationRating: (value: number) => void;
  onChangeQualityRating: (value: number) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  open,
  productName,
  productImage,
  price,
  date,
  productId,
  loading,
  rating,
  title,
  comment,
  onChangeRating,
  onChangeTitle,
  onChangeComment,
  userShowAlias,
  onChangeUserShowAlias,
  priceRating,
  accuracyRating,
  deliveryRating,
  packagingRating,
  communicationRating,
  qualityRating,
  onChangePriceRating,
  onChangeAccuracyRating,
  onChangeDeliveryRating,
  onChangePackagingRating,
  onChangeCommunicationRating,
  onChangeQualityRating,
  onClose,
  onSubmit,
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Typography variant="h6" fontWeight="bold">Leave a Review</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <ButtonBase
              component="a"
              href={productId ? `/product/${productId}` : undefined}
              target="_blank"
              rel="noopener"
              disableRipple
              sx={{ display: 'flex', alignItems: 'center', gap: 1, borderRadius: 1, pr: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
            >
              {productImage && (
                <Avatar src={productImage} variant="rounded" sx={{ width: 32, height: 32 }} />
              )}
              {productName && (
                <Typography variant="body2" sx={{ opacity: 0.95, textDecoration: 'underline' }}>
                  {productName}
                </Typography>
              )}
            </ButtonBase>
            {typeof price === 'number' && (
              <Chip size="small" label={`$${price}`} sx={{ ml: 'auto', bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Summary row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight="bold">Your Rating</Typography>
            <Rating
              name="purchase-rating"
              value={rating}
              readOnly
              precision={0.1}
              size="large"
            />
            {!!rating && (
              <Chip color="primary" label={`${rating.toFixed(1)} / 5`} sx={{ fontWeight: 700 }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <Typography variant="caption">1 = Poor</Typography>
              <Typography variant="caption">•</Typography>
              <Typography variant="caption">5 = Excellent</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            {date && (
              <Chip size="small" variant="outlined" label={`Purchased by you on ${new Date(date).toLocaleDateString()}`} />
            )}
            {productId && (
              <Button component="a" href={`/product/${productId}`} target="_blank" rel="noopener" variant="outlined" size="small" sx={{ ml: 1 }}>
                View Product
              </Button>
            )}
          </Box>
          <Divider />
          {/* Category ratings */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Pricing fairness</Typography>
              <Rating precision={1} name="price-rating" value={priceRating} onChange={(_, v) => onChangePriceRating(v || 0)} />
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>As advertised</Typography>
              <Rating precision={1} name="accuracy-rating" value={accuracyRating} onChange={(_, v) => onChangeAccuracyRating(v || 0)} />
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Delivery experience</Typography>
              <Rating precision={1} name="delivery-rating" value={deliveryRating} onChange={(_, v) => onChangeDeliveryRating(v || 0)} />
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Packaging quality</Typography>
              <Rating precision={1} name="packaging-rating" value={packagingRating} onChange={(_, v) => onChangePackagingRating(v || 0)} />
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Seller communication</Typography>
              <Rating precision={1} name="communication-rating" value={communicationRating} onChange={(_, v) => onChangeCommunicationRating(v || 0)} />
            </Box>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Product quality</Typography>
              <Rating precision={1} name="quality-rating" value={qualityRating} onChange={(_, v) => onChangeQualityRating(v || 0)} />
            </Box>
          </Box>
          <TextField
            label="Title (optional)"
            value={title}
            onChange={(e) => onChangeTitle(e.target.value)}
            fullWidth
          />
          <TextField
            label="Comment (optional)"
            value={comment}
            onChange={(e) => onChangeComment(e.target.value)}
            fullWidth
            multiline
            minRows={4}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Display your alias</Typography>
              <Typography variant="caption" color="text.secondary">If off, your review will appear as “Buyer”.</Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={userShowAlias} onChange={(e) => onChangeUserShowAlias(e.target.checked)} />}
              label={userShowAlias ? 'On' : 'Off'}
              sx={{ m: 0 }}
            />
          </Box>
          <Box sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.300',
          }}>
            <Typography variant="body2" color="text.secondary">
              Be honest and constructive. Reviews help other buyers and support our community guidelines.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">Cancel</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={loading || !rating}
          sx={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
            textTransform: 'none',
            fontWeight: 'bold',
          }}
        >
          {loading ? 'Submitting…' : 'Submit Review'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewModal;


